/**
 * Google authentication composable
 * This help us use browser supported google authentication to log in
 *
 * @see https://developers.google.com/identity/gsi/web
 * @see https://developers.google.com/identity/protocols/oauth2
 *
 * NOTE: The Google Client ID is connected to technical@eqtiming.com
 */

import { JsonWebToken } from '@/objects/generated';
import { onMounted, ref } from 'vue';
import type * as types from '@/composeables/google/types';
import { useToastStore } from '@/stores';
import { GetAppsetting } from '../config/config';

// We need to declare the google object in the global scope. This is only for typescript
declare global {
    interface Window {
        google: types.Google;
    }
}

// Global helper to check if the google api is loaded
const googleApiLoaded = ref(false);

export function useGoogleAuthentication(login: (jwt: JsonWebToken) => void) {
    /**
     * Load the google api script async in the background
     */
    const loadApi = () => {
        if (!googleApiLoaded.value) {
            const script = document.createElement('script');

            script.addEventListener('load', () => {
                googleApiLoaded.value = true;
                showChromeLoginApiLogin();
            });
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        }
    };

    /**
     * Show the chrome login api login dialog. This is the recommended way to login with google
     */
    const showChromeLoginApiLogin = () => {
        window.google.accounts.id.initialize({
            client_id: GetAppsetting('google_client_id'),
            use_fedcm_for_prompt: true,
            callback: async (res: any) => {
                // Run the callback with the jwt token

                const token = JsonWebToken.FromJsValues({
                    Payload: res.credential,
                    Provider: 'FedCM',
                });

                await login(token);
            },
        });

        // Show the login prompt
        window.google.accounts.id.prompt();
    };

    /**
     * Show the login dialog. This is the fallback if the chrome login api is not available
     * or the user is not using chrome
     * This is the old way to login with google
     */
    const showLoginDialog = () => {
        window.google.accounts.oauth2
            .initTokenClient({
                client_id: GetAppsetting('google_client_id'),
                scope: 'email profile openid',
                callback: (response: any) => {
                    if (response.access_token) {
                        // Run the callback with the access token
                        const token = JsonWebToken.FromJsValues({
                            Payload: response.access_token,
                            Provider: 'Google',
                        });
                        login(token);
                    } else {
                        useToastStore().addError('access.notauthorized');
                    }
                },
            })
            .requestAccessToken();
    };

    // Load the api on mount
    onMounted(() => {
        loadApi();
    });

    /**
     * Return the showLoginDialog function and the googleApiLoaded ref
     */
    return {
        /**
         * Show the login dialog
         */
        showLoginDialog,
        /**
         * Ref to check if the google api is loaded
         */
        isLoaded: googleApiLoaded,
    };
}
